import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
} from "@/components/ui/input-otp";
import { Mail, Lock, Loader2, Eye, EyeOff, ShieldCheck, KeyRound, ArrowLeft } from "lucide-react";
import { AppLogo, AppBrandingText } from "@/components/app-logo";
import { useAppBranding } from "@/contexts/app-branding";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { Link } from "wouter";
import { consumeSessionExpiredFlag, isValidReturnTo } from "@/lib/auth-utils";
import { usePageTitle } from "@/hooks/use-page-title";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function Login() {
  usePageTitle("Login");
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { appName } = useAppBranding();
  const [showTotpForm, setShowTotpForm] = useState(false);
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showSplash, setShowSplash] = useState(false);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(true);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const searchParams = new URLSearchParams(window.location.search);
  const returnTo = searchParams.get("returnTo");
  const finalDestination = isValidReturnTo(returnTo) ? returnTo! : "/dashboard";

  useEffect(() => {
    const expired = consumeSessionExpiredFlag();
    if (expired) {
      toast({
        title: "Session expired",
        description: "Your session has expired. Please sign in again.",
        variant: "destructive",
      });
    }
  }, []);

  useEffect(() => {
    return () => {
      setChallengeToken(null);
      setTotpCode("");
      setRecoveryCode("");
      setRememberDevice(false);
    };
  }, []);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const syncAutofillValues = () => {
    const emailValue = emailRef.current?.value;
    const passwordValue = passwordRef.current?.value;
    if (emailValue && !form.getValues("email")) {
      form.setValue("email", emailValue, { shouldValidate: false });
    }
    if (passwordValue && !form.getValues("password")) {
      form.setValue("password", passwordValue, { shouldValidate: false });
    }
  };

  const handleAutofillAnimation = (e: React.AnimationEvent<HTMLInputElement>) => {
    if (e.animationName === "onAutoFillStart") {
      syncAutofillValues();
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      syncAutofillValues();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const loginMutation = useMutation({
    mutationFn: async (data: LoginFormData) => {
      return apiRequest("POST", "/api/auth/login", data);
    },
    onSuccess: async (response) => {
      if (response.requireTotp) {
        setChallengeToken(response.challengeToken);
        setShowTotpForm(true);
        setTotpCode("");
        setRecoveryCode("");
        setUseRecoveryCode(false);
      } else {
        toast({
          title: "Login successful",
          description: `Welcome back to ${appName}`,
        });
        setShowSplash(true);
        await new Promise(resolve => setTimeout(resolve, 2500));
        await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
        navigate(finalDestination);
      }
    },
    onError: (error: any) => {
      toast({
        title: "Login failed",
        description: `${error.message || "Invalid email or password"}. If you need help, please contact your administrator.`,
        variant: "destructive",
      });
    },
  });

  const totpMutation = useMutation({
    mutationFn: async () => {
      const code = useRecoveryCode ? recoveryCode.trim() : totpCode;
      return apiRequest("POST", "/api/auth/totp-verify", {
        challengeToken,
        code,
        rememberDevice,
      });
    },
    onSuccess: async () => {
      setChallengeToken(null);
      toast({
        title: "Login successful",
        description: `Welcome back to ${appName}`,
      });
      setShowSplash(true);
      await new Promise(resolve => setTimeout(resolve, 2500));
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      navigate(finalDestination);
    },
    onError: (error: any) => {
      toast({
        title: "Verification failed",
        description: error.message || "Invalid code. Please try again.",
        variant: "destructive",
      });
      if (useRecoveryCode) {
        setRecoveryCode("");
      } else {
        setTotpCode("");
      }
    },
  });

  const onSubmitLogin = (data: LoginFormData) => {
    loginMutation.mutate(data);
  };

  const handleTotpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!challengeToken) return;
    const code = useRecoveryCode ? recoveryCode.trim() : totpCode;
    if (!code) return;
    if (!useRecoveryCode && !/^\d{6}$/.test(code)) return;
    totpMutation.mutate();
  };

  const handleBackToLogin = () => {
    setShowTotpForm(false);
    setChallengeToken(null);
    setTotpCode("");
    setRecoveryCode("");
    setUseRecoveryCode(false);
    setRememberDevice(false);
  };

  if (showSplash) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background animate-in fade-in duration-300" data-testid="loading-screen">
        <div className="animate-pulse">
          <AppLogo size="xl" />
        </div>
        <div className="flex flex-col items-center gap-2 mt-6">
          <AppBrandingText className="text-2xl" />
          <p className="text-sm text-muted-foreground">Loading your workspace...</p>
        </div>
        <div className="flex gap-1.5 mt-4">
          <span className="h-2 w-2 rounded-full bg-primary animate-bounce [animation-delay:0ms]" />
          <span className="h-2 w-2 rounded-full bg-primary animate-bounce [animation-delay:150ms]" />
          <span className="h-2 w-2 rounded-full bg-primary animate-bounce [animation-delay:300ms]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <AppLogo size="2xl" />
          </div>
          <h1 className="text-3xl" data-testid="text-login-title">
            <AppBrandingText />
          </h1>
          <p className="text-muted-foreground mt-2" data-testid="text-login-subtitle">Behavior Evals, Tracking, & Insights</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {showTotpForm ? (
                <>
                  <ShieldCheck className="h-5 w-5" />
                  Two-Factor Authentication
                </>
              ) : (
                "Sign In"
              )}
            </CardTitle>
            <CardDescription>
              {showTotpForm
                ? useRecoveryCode
                  ? "Enter one of your recovery codes"
                  : "Enter the 6-digit code from your authenticator app"
                : "Enter your email and password to access the system"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!showTotpForm ? (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmitLogin)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              {...field}
                              ref={(e) => {
                                field.ref(e);
                                (emailRef as React.MutableRefObject<HTMLInputElement | null>).current = e;
                              }}
                              type="email"
                              placeholder="Enter your email address..."
                              className="pl-10"
                              data-testid="input-email"
                              disabled={loginMutation.isPending}
                              onAnimationStart={handleAutofillAnimation}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              {...field}
                              ref={(e) => {
                                field.ref(e);
                                (passwordRef as React.MutableRefObject<HTMLInputElement | null>).current = e;
                              }}
                              type={showPassword ? "text" : "password"}
                              placeholder="Enter your password..."
                              className="pl-10 pr-10"
                              data-testid="input-password"
                              disabled={loginMutation.isPending}
                              onAnimationStart={handleAutofillAnimation}
                            />
                            <button
                              type="button"
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                              onClick={() => setShowPassword(!showPassword)}
                              disabled={loginMutation.isPending}
                              data-testid="button-toggle-password"
                              tabIndex={-1}
                            >
                              {showPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loginMutation.isPending}
                    data-testid="button-login"
                  >
                    {loginMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      "Sign In"
                    )}
                  </Button>

                  <div className="text-center">
                    <Link 
                      href="/forgot-password" 
                      className="text-sm text-muted-foreground hover:text-primary"
                      data-testid="link-forgot-password"
                    >
                      Forgot your password?
                    </Link>
                  </div>
                </form>
              </Form>
            ) : (
              <form onSubmit={handleTotpSubmit} className="space-y-4">
                {!useRecoveryCode ? (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Your authenticator app should be showing a 6-digit code that changes every 30 seconds.
                    </p>
                    <div className="flex justify-center py-2">
                      <InputOTP
                        maxLength={6}
                        value={totpCode}
                        onChange={(value) => setTotpCode(value)}
                        disabled={totpMutation.isPending}
                        data-testid="input-totp-code"
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                        </InputOTPGroup>
                        <InputOTPSeparator />
                        <InputOTPGroup>
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Enter one of the recovery codes you saved when you set up two-factor authentication.
                    </p>
                    <div className="relative">
                      <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="text"
                        value={recoveryCode}
                        onChange={(e) => setRecoveryCode(e.target.value)}
                        placeholder="xxxxx-xxxxx"
                        className="pl-10 font-mono text-center tracking-wider"
                        disabled={totpMutation.isPending}
                        data-testid="input-recovery-code"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="remember-device"
                    checked={rememberDevice}
                    onCheckedChange={(checked) => setRememberDevice(checked === true)}
                    disabled={totpMutation.isPending}
                    data-testid="checkbox-remember-device"
                  />
                  <label
                    htmlFor="remember-device"
                    className="text-sm text-muted-foreground cursor-pointer select-none"
                  >
                    Remember this device
                  </label>
                </div>

                <div className="space-y-2">
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={totpMutation.isPending || (!useRecoveryCode && totpCode.length !== 6) || (useRecoveryCode && !recoveryCode.trim())}
                    data-testid="button-verify-totp"
                  >
                    {totpMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      "Verify"
                    )}
                  </Button>

                  <div className="flex flex-col items-center gap-2 pt-2">
                    <button
                      type="button"
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                      onClick={() => {
                        setUseRecoveryCode(!useRecoveryCode);
                        setTotpCode("");
                        setRecoveryCode("");
                      }}
                      disabled={totpMutation.isPending}
                      data-testid="button-toggle-recovery"
                    >
                      {useRecoveryCode ? "Use authenticator code instead" : "Use a recovery code instead"}
                    </button>
                    
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleBackToLogin}
                      disabled={totpMutation.isPending}
                      data-testid="button-back-to-login"
                    >
                      <ArrowLeft className="mr-1 h-3 w-3" />
                      Back to login
                    </Button>
                  </div>
                </div>

                {useRecoveryCode && (
                  <p className="text-xs text-muted-foreground text-center pt-1">
                    If you've lost access to your authenticator app and don't have any recovery codes, contact a super admin. They can reset your two-factor authentication so you can set it up again.
                  </p>
                )}
              </form>
            )}

            <div className="mt-6 pt-6 border-t text-center">
              <p className="text-sm text-muted-foreground">
                Have an invitation code?{" "}
                <Link href="/invite" className="text-primary hover:underline">
                  Create your account
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
